import { createFileRoute } from "@tanstack/react-router";
import { DM_Carpet } from "common/materials/library/designMaterialPath";
import { TemplateIdToName } from "common/materials/library/templateIdToName";
import { countMaterialSrcsForTemplate, materialSrcsForTemplate } from "common/materials/materialLibraryUtil";
import type { MaterialSrc } from "common/materials/materialSrc";
import { pathStringFromMaterialSrc } from "common/materials/materialSrcFromPathString";
import { downloadBlob } from "common/utils/downloadFile";
import { initSharedWebGL, WebGLContextReuse } from "gfx/activeSharedWebGL";
import type { ThumbnailLightOptions, ThumbnailOptions } from "gfx/materials/designMaterialRenderImage";
import {
  defaultThumbnailLightOptions,
  designMaterialRenderImageOptimized,
} from "gfx/materials/designMaterialRenderImage";
import { BakeColorTextureMode } from "gfx/materials/designMaterialRuntimeConfig";
import { logMaterialSrc } from "gfx/materials/logMaterialSrc";
import { defaultRendererParams } from "gfx/threejs/higharc/SSFXRendererParams";
import type { THREEViewerServices } from "gfx/THREEViewerServices";
import JSZip from "jszip";
import { useRenderedMaterialPreview } from "lib/hooks/useRenderedMaterialPreview";
import getPageTitle from "lib/utils/getPageTitle";
import { useEffect, useRef, useState } from "react";
import { Helmet as Head } from "react-helmet-async";
import withAuthentication from "../../components/withAuthentication";
import styles from "./material-thumbnails.module.scss";

const allTemplateIds: number[] = Object.keys(TemplateIdToName).map((k) => parseInt(k));

const DOWNLOAD_MAX_COUNT_LIMIT = 10000;

const ALL_TEMPLATES_LIST = "<ALL TEMPLATES>";

const defaultPreviewMaterial: MaterialSrc = DM_Carpet.L.Carpet_Texture.Carpet_Texture_Oatmeal;

function sanitizeSize(size: number, max: number = 2048) {
  if (isNaN(size)) {
    return 1;
  }
  return Math.round(Math.max(Math.min(size, max), 1));
}

function replaceFilenameIllegalChars(name: string, replacement: string) {
  const FILENAME_ILLEGAL_CHARS_RE = /[#%&\{\}\\\/\<\>\*\?\$\!\'\"\:\@\+\`\|\=]/g;
  return name.replace(FILENAME_ILLEGAL_CHARS_RE, replacement);
}

function createFilterRe(filterStr: string, requireFullMatch: boolean) {
  let reStr = filterStr;
  reStr = reStr.replace(/\*/g, ".*");
  if (requireFullMatch) {
    reStr = `^${reStr}$`;
  }
  return new RegExp(reStr, "i");
}

type FileFormat = "image/jpeg" | "image/png";

function MaterialThumbnailTest() {
  const [previewMaterial, setPreviewMaterial] = useState<MaterialSrc | undefined>(defaultPreviewMaterial);
  const [previewMaterialName, setPreviewMaterialName] = useState<string>(logMaterialSrc(defaultPreviewMaterial));

  const [lightOptions, setLightOptions] = useState<ThumbnailLightOptions>({ ...defaultThumbnailLightOptions });

  function setLightOption(opt: Partial<ThumbnailLightOptions>) {
    setLightOptions({ ...lightOptions, ...opt });
  }

  const [downloadMaxCount, setDownloadMaxCount] = useState<number>(Math.min(1000, DOWNLOAD_MAX_COUNT_LIMIT));
  const [downloadStartIndex, setDownloadStartIndex] = useState<number>(0);
  const [filenameExample, setFilenameExample] = useState<string>(".jpg");
  const [fileFormat, setFileFormat] = useState<FileFormat>("image/jpeg");
  const [worldWidth, setWorldWidth] = useState<number>(40);
  const [imageWidth, setImageWidth] = useState<number>(200);
  const [imageHeight, setImageHeight] = useState<number>(200);
  const [namePrefix, setNamePrefix] = useState<string>("");
  const [nameSuffix, setNameSuffix] = useState<string>("");
  const [filterStr, setFilterStr] = useState<string>("");
  const [usingRelaxedRe, setUsingRelaxedRe] = useState<boolean>(true);
  const [templateIds, setTemplateIds] = useState<number[]>([...allTemplateIds]);
  const [idList, setIdList] = useState<string>(ALL_TEMPLATES_LIST);

  const previewCanvasRef = useRef<HTMLCanvasElement>(null);

  function getFilename(materialPathName: string): string {
    const name =
      namePrefix + materialPathName.replace(/\./g, "_") + nameSuffix + (fileFormat === "image/png" ? ".png" : ".jpg");
    return replaceFilenameIllegalChars(name, "_");
  }

  function getOptions(): ThumbnailOptions {
    return {
      width: sanitizeSize(imageWidth),
      height: sanitizeSize(imageHeight),
      worldWidth: sanitizeSize(worldWidth, 200),
      ...lightOptions,
    };
  }

  function downloadMaterialSrcList(): MaterialSrc[] {
    const materialIdSet: MaterialSrc[] = [];
    let skipLeft = downloadStartIndex;
    for (const templateId of templateIds) {
      const countLeft = downloadMaxCount - materialIdSet.length;
      if (countLeft <= 0) {
        break;
      }
      if (skipLeft > 0) {
        const countForTemplate = countMaterialSrcsForTemplate(templateId);
        if (skipLeft >= countForTemplate) {
          skipLeft -= countForTemplate;
          continue;
        }
      }
      materialIdSet.push(...materialSrcsForTemplate(templateId, countLeft, skipLeft));
      skipLeft = 0;
    }
    return materialIdSet;
  }

  async function download() {
    if (templateIds.length === 0) {
      return;
    }

    const materialIdSet: MaterialSrc[] = downloadMaterialSrcList();

    const services: THREEViewerServices = initSharedWebGL({
      reuse: WebGLContextReuse.AUTO,
      onScreenRenderingDisabled: true,
    }).services;

    services.httpMaterialFactory.setBakeColorTexture(BakeColorTextureMode.DONT_BAKE);

    console.time("load material parameters");

    const materialIdsToLoad = materialIdSet.filter(
      (id) => !services.httpMaterialFactory.designMaterialIsFetched(id, true),
    );
    if (materialIdsToLoad.length > 0) {
      await Promise.all(materialIdsToLoad.map((matid) => services.httpMaterialFactory.getDesignMaterial(matid, true)));
    }

    console.timeEnd("load material parameters");

    console.time("render material images");

    const options = getOptions();

    let finishedImageCount = 0;

    const zip = new JSZip();

    for (const materialId of materialIdSet) {
      const material = await services.httpMaterialFactory.getDesignMaterial(materialId, true);
      if (!material) {
        continue;
      }
      ((mat, name) => {
        name = getFilename(name);
        console.log("rendering " + name);
        const renderParams = defaultRendererParams(services.capabilities);
        designMaterialRenderImageOptimized(mat, services.imageRenderer, renderParams, options, (image) => {
          image.toBlob((blob) => {
            if (blob) {
              zip.file(name, blob);
              ++finishedImageCount;
            }
          }, fileFormat);
        });
      })(material, pathStringFromMaterialSrc(materialId));
    }

    const waitForBlobs = (resolve) => {
      if (finishedImageCount === materialIdSet.length) {
        resolve();
        return;
      }
      setTimeout(() => waitForBlobs(resolve), 100);
    };

    await new Promise((resolve) => waitForBlobs(resolve));

    const zipFile: Blob = (await zip.generateAsync({ type: "blob" })) as Blob;
    console.timeEnd("render material images");
    let zipFileName =
      namePrefix +
      "material-thumbnails" +
      nameSuffix +
      (downloadStartIndex > 0 ? "-" + downloadStartIndex : "") +
      ".zip";
    zipFileName = replaceFilenameIllegalChars(zipFileName, "_");
    downloadBlob(zipFileName, zipFile);
  }

  useEffect(() => {
    let newUsingRelaxedRe = false;
    if (filterStr === "" || filterStr === "*") {
      setTemplateIds([...allTemplateIds]);
      setIdList(ALL_TEMPLATES_LIST);
    } else {
      const filterRe = createFilterRe(filterStr, true);

      let templateIdSet = [...allTemplateIds].filter((id) => TemplateIdToName[id].match(filterRe) !== null);

      if (templateIdSet.length === 0) {
        const filterReRelaxed = createFilterRe(filterStr, false);
        newUsingRelaxedRe = true;
        templateIdSet = [...allTemplateIds].filter((id) => TemplateIdToName[id].match(filterReRelaxed) !== null);
      }

      setTemplateIds(templateIdSet);

      const idListLimit = 100;
      let idListStr = "";

      for (let i = 0, l = Math.min(idListLimit, templateIdSet.length); i < l; ++i) {
        idListStr += TemplateIdToName[templateIdSet[i]] + "\n";
      }

      if (templateIdSet.length > idListLimit) {
        idListStr += `...+${templateIdSet.length - idListLimit} more...\n`;
      }
      setIdList(idListStr);

      if (templateIdSet.length > 0) {
        const previewMaterials = materialSrcsForTemplate(templateIdSet[0], 1, 0);
        if (previewMaterials.length > 0) {
          setPreviewMaterial(previewMaterials[0]);
          setPreviewMaterialName(logMaterialSrc(previewMaterials[0]));
        }
      }
    }
    setUsingRelaxedRe(newUsingRelaxedRe);
  }, [filterStr]);

  useEffect(() => {
    if (previewMaterial) {
      const materialPath = pathStringFromMaterialSrc(previewMaterial);
      setFilenameExample(getFilename(materialPath));
    }
  }, [previewMaterial, nameSuffix, namePrefix, fileFormat]);

  useRenderedMaterialPreview(previewMaterial, previewCanvasRef, getOptions());

  return (
    <>
      <Head>
        <title>{getPageTitle("Material Thumbnail Generation")}</title>
        <meta name="robots" content="noindex" />
      </Head>
      <div>
        <div className={styles.ui}>
          <h2>Files</h2>
          <p>
            Template filter: <input type="text" size={50} onChange={(e) => setFilterStr(e.currentTarget.value)} />
          </p>
          <p>
            {usingRelaxedRe
              ? "using relaxed filter - any part of the template name can match"
              : "using strict filter - whole template name needs to match"}
          </p>
          <p>
            <b>{templateIds.length}</b> matches found{" "}
            {templateIds.length > downloadMaxCount ? (
              <span>
                - note that the download is limited to {downloadMaxCount} materials at a time! Downloading lots of
                materials may take a long time.
              </span>
            ) : (
              ""
            )}
          </p>
          <textarea className={styles.materialList} readOnly value={idList}></textarea>
          <p>
            Filename prefix:{" "}
            <input type="text" value={namePrefix} onChange={(e) => setNamePrefix(e.currentTarget.value)}></input>
          </p>
          <p>
            Filename suffix:{" "}
            <input type="text" value={nameSuffix} onChange={(e) => setNameSuffix(e.currentTarget.value)}></input>
          </p>
          <p>Filename example: {filenameExample}</p>
          <h2>Image settings</h2>
          <p>
            File format:{" "}
            <select
              value={fileFormat}
              onChange={(e) => {
                setFileFormat(e.currentTarget.value as "image/jpeg" | "image/png");
              }}
            >
              <option value="image/jpeg">JPEG</option>
              <option value="image/png">PNG</option>
            </select>
          </p>
          <p>
            Bitmap width:{" "}
            <input
              type="number"
              min="16"
              max="2048"
              step="1"
              value={imageWidth}
              onChange={(e) => {
                setImageWidth(e.currentTarget.valueAsNumber);
              }}
            />
          </p>
          <p>
            Bitmap height:{" "}
            <input
              type="number"
              min="16"
              max="2048"
              step="1"
              value={imageHeight}
              onChange={(e) => {
                setImageHeight(e.currentTarget.valueAsNumber);
              }}
            />
          </p>
          <p>
            Width in inches:{" "}
            <input
              type="number"
              min="1"
              max="200"
              step="1"
              value={worldWidth}
              onChange={(e) => {
                setWorldWidth(e.currentTarget.valueAsNumber);
              }}
            />
          </p>
          <h2>Lighting settings</h2>
          <h3>Directional light:</h3>
          <p>
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={lightOptions.directionalLightIntensity}
              onChange={(e) => setLightOption({ directionalLightIntensity: e.currentTarget.valueAsNumber })}
            />{" "}
            {lightOptions.directionalLightIntensity}
          </p>
          <p>
            <input
              type="text"
              value={lightOptions.directionalLightColor}
              onChange={(e) => setLightOption({ directionalLightColor: e.currentTarget.value })}
            />
          </p>
          <h3>Ambient light:</h3>
          <p>
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={lightOptions.ambientLightIntensity}
              onChange={(e) => setLightOption({ ambientLightIntensity: e.currentTarget.valueAsNumber })}
            />{" "}
            {lightOptions.ambientLightIntensity}
          </p>
          <p>
            <input
              type="text"
              value={lightOptions.ambientLightColor}
              onChange={(e) => setLightOption({ ambientLightColor: e.currentTarget.value })}
            />
          </p>
          <h3>Light probe:</h3>
          <p>
            Top:{" "}
            <input
              type="text"
              value={lightOptions.gradientTopColor}
              onChange={(e) => {
                setLightOption({ gradientTopColor: e.currentTarget.value });
              }}
            />
          </p>
          <p>
            Middle:{" "}
            <input
              type="text"
              value={lightOptions.gradientMiddleColor}
              onChange={(e) => {
                setLightOption({ gradientMiddleColor: e.currentTarget.value });
              }}
            />
          </p>
          <p>
            Bottom:{" "}
            <input
              type="text"
              value={lightOptions.gradientBottomColor}
              onChange={(e) => {
                setLightOption({ gradientBottomColor: e.currentTarget.value });
              }}
            />
          </p>
          <h2>Download</h2>
          <p>
            Download start index:{" "}
            <input
              type="number"
              min="0"
              value={downloadStartIndex}
              onChange={(e) => {
                setDownloadStartIndex(e.currentTarget.valueAsNumber);
              }}
            />
          </p>
          <p>
            Download max count:{" "}
            <input
              type="number"
              min="1"
              max={DOWNLOAD_MAX_COUNT_LIMIT}
              value={downloadMaxCount}
              onChange={(e) => {
                setDownloadMaxCount(Math.min(DOWNLOAD_MAX_COUNT_LIMIT, e.currentTarget.valueAsNumber));
              }}
            />
          </p>
          <input type="button" onClick={() => download()} value="Download material thumbnails" />
        </div>
        <div className={styles.preview}>
          <canvas ref={previewCanvasRef} width="300" height="300"></canvas>
          <br />
          {previewMaterialName}
        </div>
      </div>
    </>
  );
}

function MaterialThumbnailTestPage() {
  return <MaterialThumbnailTest />;
}

export const Route = createFileRoute("/debug/material-thumbnails")({
  component: withAuthentication(MaterialThumbnailTestPage, { superAdmin: true }),
});
